import { Grid, GridProps } from '@material-ui/core'
import flatMap from 'lodash/flatMap'
import groupBy from 'lodash/groupBy'
import { DateTime, Interval } from 'luxon'
import React, { useContext, useEffect, useState } from 'react'
import styled, { css, DefaultTheme } from 'styled-components'

import Timeslot, { TimeslotProps } from '../timeslot/Timeslot'
import { Body } from '../utils/typography'
import LiveNowContext from './LiveNowContext'

const getLiveColor = (property: 'background' | 'color') => css`
  ${({ theme, live = false }: { live?: boolean; theme: DefaultTheme }) =>
    live
      ? css`
          ${property}: ${theme.palette.live.main};
        `
      : ''}
`

const liveProp = {
  background: getLiveColor('background'),
  color: getLiveColor('color'),
}

const TimeslotGroupGrid = styled(Grid)`
  ${({ theme: { spacing } }) => css`
    justify-content: flex-end;
    margin-bottom: ${spacing?.(1)}px;
  `}
`

const TimeDividerLine = styled.div`
  ${({ theme: { palette } }) => css`
    background: ${palette.grey[300]};
    flex-grow: 1;
    height: 2px;
  `}
`

const AlteredBody = styled(Body)``

type TimeDividerContainerProps = { live?: boolean } & GridProps

const TimeDividerContainer = styled(
  ({ live, children, ...otherProps }: TimeDividerContainerProps) => {
    return <Grid {...otherProps}>{children}</Grid>
  }
)`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  align-items: center;
  ${AlteredBody} {
    ${liveProp.color}
  }
  ${TimeDividerLine} {
    ${liveProp.background}
  }
`

const formatTime = (date: DateTime) =>
  date.toLocaleString({
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
  })

const TimeslotGroup = ({ timeslots }: { timeslots: TimeslotProps[] }) => {
  const [liveTime, setLiveTime] = useState(DateTime.local())
  const { liveNow } = useContext(LiveNowContext)

  useEffect(() => {
    const counter = setInterval(() => setLiveTime(DateTime.local()), 30000)

    return () => clearInterval(counter)
  }, [])

  const getIsLive = (interval: Interval) => interval.contains(liveTime)

  const groupedTimeslots = groupBy(
    timeslots
      .filter(({ endsAt }) => !liveNow || endsAt >= liveTime)
      .map(timeslot => ({
        ...timeslot,
        live: getIsLive(Interval.fromDateTimes(timeslot.startsAt, timeslot.endsAt)),
      })),
    ({ startsAt }) => startsAt.toISO()
  )

  const timeslotGroupComponents = flatMap(groupedTimeslots, (innerTimeslot, key) => (
    <React.Fragment key={`timeslotGroup$${key}`}>
      <TimeDividerContainer
        key={`timeslotDivider$${key}`}
        item
        id={`timeslotDivider$${key}`}
        live={innerTimeslot.find(timeslot => timeslot.live === true) !== undefined}
        xs={12}
      >
        <AlteredBody mr={2} my={1}>
          {formatTime(DateTime.fromISO(key, { setZone: true }))}
        </AlteredBody>
        <TimeDividerLine />
      </TimeDividerContainer>
      {innerTimeslot
        .sort((a, b) => a.location.name.localeCompare(b.location.name))
        .map(
          ({
            endsAt,
            format,
            location,
            participants,
            slug,
            startsAt,
            title,
            topics,
            liveURL,
            videoURL,
            live,
            tags,
          }) => (
            <Grid key={`timeslotItem$${slug}`} item id={`timeslotItem$${slug}`} xs={11}>
              <Timeslot
                endsAt={endsAt}
                format={format}
                live={live}
                liveURL={liveURL}
                location={location}
                participants={participants}
                slug={slug}
                startsAt={startsAt}
                tags={tags}
                title={title}
                topics={topics}
                videoURL={videoURL}
              />
            </Grid>
          )
        )}
    </React.Fragment>
  ))

  return (
    <TimeslotGroupGrid container spacing={1}>
      {timeslotGroupComponents}
    </TimeslotGroupGrid>
  )
}

export default TimeslotGroup
